/**
 * Typer + zod-validering för config/*.json. All balans ligger i config —
 * sim-koden innehåller inga hårdkodade balanstal.
 */
import { z } from 'zod';

export const FAMILIES = ['swarm', 'ranged', 'armor', 'antiarmor', 'siege', 'flyer'] as const;
export type Family = (typeof FAMILIES)[number];

const unitTierSchema = z.object({
  id: z.string(),
  name: z.string(),
  hp: z.number().positive(),
  damage: z.number().positive(),
  attackIntervalSec: z.number().positive(),
  range: z.number().positive(),
  speed: z.number().positive(),
  splashRadius: z.number().positive().optional(),
});

const familySchema = z.object({
  flying: z.boolean().optional(),
  tiers: z.array(unitTierSchema).length(3),
});

export const unitsConfigSchema = z.object({
  $comment: z.string().optional(),
  families: z.object({
    swarm: familySchema,
    ranged: familySchema,
    armor: familySchema,
    antiarmor: familySchema,
    siege: familySchema,
    flyer: familySchema,
  }),
});

export const countersConfigSchema = z.object({
  $comment: z.string().optional(),
  counterMultiplier: z.number(),
  counteredMultiplier: z.number(),
  siegeVsBuildingMultiplier: z.number(),
  matrix: z.record(z.enum(FAMILIES), z.record(z.enum(FAMILIES), z.number())),
  canTargetAir: z.array(z.enum(FAMILIES)),
});

const pointSchema = z.object({ x: z.number(), y: z.number() });

export const matchConfigSchema = z.object({
  $comment: z.string().optional(),
  simTicksPerSecond: z.number().int().positive(),
  baseSpawnIntervalSec: z.number().positive(),
  minSpawnIntervalSec: z.number().positive(),
  baseHealth: z.number().positive(),
  mapSize: z.object({ width: z.number().positive(), height: z.number().positive() }),
  basePositions: z.object({ p1: pointSchema, p2: pointSchema }),
  startingUnit: z.object({ family: z.enum(FAMILIES), tier: z.number().int().min(1).max(3) }),
  spawnJitterRadius: z.number().min(0),
  mothership: z.object({
    moveSpeed: z.number().positive(),
    health: z.number().positive(),
    retreatOnDamageThreshold: z.number(),
    retreatDurationSec: z.number(),
    spawnPausedDuringRetreat: z.boolean(),
  }),
  suddenDeath: z.object({
    startAtSec: z.number(),
    baseDecayPctPer10Sec: z.number(),
    decayAccelerationPctPerMinute: z.number(),
  }),
  maxArmySize: z.number().int().positive(),
  counterWarningThreshold: z.number(),
  flocking: z.object({
    separationRadius: z.number().positive(),
    separationStrength: z.number().min(0),
    followSlackRadius: z.number().min(0),
    shipClearanceRadius: z.number().min(0),
  }),
});

const upgradeSchema = z.object({
  baseCost: z.number(),
  growth: z.number(),
  effectPerLevel: z.record(z.string(), z.number()),
  maxLevel: z.number().int(),
});

export const economyConfigSchema = z.object({
  $comment: z.string().optional(),
  passiveIncomePerSec: z.number(),
  killBounty: z.object({ tier1: z.number(), tier2: z.number(), tier3: z.number() }),
  underdogIncomeBonus: z.number(),
  underdogMetric: z.string(),
  startingCredits: z.number(),
  upgrades: z.object({
    income: upgradeSchema,
    damage: upgradeSchema,
    health: upgradeSchema,
    spawnRate: upgradeSchema,
  }),
  evolution: z.object({
    tier2Cost: z.number(),
    tier3Cost: z.number(),
    switchFamilyCost: z.number(),
    keepUnlockedTiersPerFamily: z.boolean(),
  }),
});

export type UnitTierConfig = z.infer<typeof unitTierSchema>;
export type UnitsConfig = z.infer<typeof unitsConfigSchema>;
export type CountersConfig = z.infer<typeof countersConfigSchema>;
export type MatchConfig = z.infer<typeof matchConfigSchema>;
export type EconomyConfig = z.infer<typeof economyConfigSchema>;

export interface SimConfig {
  units: UnitsConfig;
  counters: CountersConfig;
  match: MatchConfig;
  economy: EconomyConfig;
}

/** Validerar råa JSON-objekt (från import eller fs) och returnerar typad config. */
export function loadConfig(raw: {
  units: unknown;
  counters: unknown;
  match: unknown;
  economy: unknown;
}): SimConfig {
  return {
    units: unitsConfigSchema.parse(raw.units),
    counters: countersConfigSchema.parse(raw.counters),
    match: matchConfigSchema.parse(raw.match),
    economy: economyConfigSchema.parse(raw.economy),
  };
}

/** Slår upp stats för (family, tier). tier är 1-baserad. */
export function getUnitTier(units: UnitsConfig, family: Family, tier: number): UnitTierConfig {
  const t = units.families[family].tiers[tier - 1];
  if (!t) throw new Error(`Okänd unit: ${family} tier ${tier}`);
  return t;
}
